import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Placeholder,
  Spinner,
} from "react-bootstrap";
import { HeadPage } from "src/componentes";
import CTeVisualizaCode from "./CTe.Visualiza.Code";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faPrint,
  faCopy,
  faTruck,
  faRoute,
  faUsers,
  faBoxes,
  faFileInvoice,
  faDollarSign,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const CTeVisualizaView = () => {
  const code = CTeVisualizaCode();
  const cte = code.cte;

  if (code.loading) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar CT-e" />
        <Placeholder animation="glow" className="mt-4">
          <Placeholder xs={12} size="lg" className="mb-3" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
        </Placeholder>
      </Container>
    );
  }

  if (!cte) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar CT-e" />
        <Card className="mt-4 text-center p-5 border-0 shadow-sm">
          <Card.Body>
            <h4 className="text-secondary mb-3">CT-e não encontrado</h4>
            <p className="text-muted mb-4">
              Não foi possível carregar as informações deste Conhecimento de Transporte.
            </p>
            <Button variant="outline-primary" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar para Listagem
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const isAutorizado = cte.autorizacao?.codigoStatusResposta === 100;

  return (
    <Container className="pb-5 container-print">
      {/* Cabeçalho superior da página */}
      <div className="no-print">
        <HeadPage headText={`CT-e: ${cte.numeroCte} / Série: ${cte.serie}`} />

        {/* Barra de Ações Rápidas */}
        <Card className="my-3 border-0 shadow-sm">
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3">
            <Button variant="outline-secondary" size="sm" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onBaixarXml}
                disabled={code.downloadingXml || !cte.id}
              >
                {code.downloadingXml ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                )}
                Baixar XML
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onImprimir}
                disabled={code.printing || !cte.id}
              >
                {code.printing ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                )}
                Imprimir DACTE
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Status da Autorização / SEFAZ */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h5 className="mb-0 text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
              Status do Documento Fiscal
            </h5>
            <div>
              {cte.autorizacao ? (
                <Badge
                  bg={isAutorizado ? "success" : "warning"}
                  className="px-3 py-2 fs-6"
                >
                  {cte.autorizacao.codigoStatusResposta} -{" "}
                  {cte.autorizacao.descricaoMotivoResposta || "Autorizado"}
                </Badge>
              ) : (
                <Badge bg="secondary" className="px-3 py-2 fs-6">
                  Não transmitido
                </Badge>
              )}
            </div>
          </div>
          <Row>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-muted small d-block">Número CT-e</span>
              <strong className="fs-5">{cte.numeroCte}</strong>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-muted small d-block">Série</span>
              <strong className="fs-5">{cte.serie}</strong>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-muted small d-block">Data/Hora Emissão</span>
              <strong>
                {cte.emissaoDataHora
                  ? code.FormatarDataHoraDefault(cte.emissaoDataHora)
                  : "-"}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Status de Envio</span>
              <span>
                {cte.autorizacao
                  ? `${cte.autorizacao.codigoStatusEnvio} - ${cte.autorizacao.descricaoStatusEnvio}`
                  : "-"}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Características & Modal do Transporte */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faTruck} className="me-2 text-primary" />
            Características e Transporte
          </h5>
          <Row>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Tipo de CT-e</span>
              <strong>{code.getTipoCteLabel(cte.tipoCte)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Tipo de Serviço</span>
              <strong>{code.getTipoServicoLabel(cte.tipoServico)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Modal de Transporte</span>
              <strong>{code.getModalLabel(cte.modal)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">RNTRC</span>
              <span>{cte.rntrc || "-"}</span>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Retira</span>
              <span>{cte.retira === 0 ? "Sim" : "Não"}</span>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Tipo de Tomador</span>
              <strong>{code.getTipoTomadorLabel(cte.tipoTomador)}</strong>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Percurso do Transporte */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faRoute} className="me-2 text-primary" />
            Percurso da Prestação
          </h5>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <div className="p-3 border rounded bg-light">
                <div className="text-muted small fw-bold text-uppercase mb-1">
                  Início da Prestação (Origem)
                </div>
                <div className="fs-5 fw-bold text-dark">
                  {cte.nomeMunicipioInicio} - {cte.ufInicio}
                </div>
                <div className="text-muted small">
                  Código IBGE: {cte.codigoMunicipioInicio || "-"}
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <div className="p-3 border rounded bg-light">
                <div className="text-muted small fw-bold text-uppercase mb-1">
                  Término da Prestação (Destino)
                </div>
                <div className="fs-5 fw-bold text-dark">
                  {cte.nomeMunicipioFim} - {cte.ufFim}
                </div>
                <div className="text-muted small">
                  Código IBGE: {cte.codigoMunicipioFim || "-"}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Atores / Participantes */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
            Participantes
          </h5>
          <Row>
            {/* Remetente */}
            <Col xs={12} lg={6} className="mb-3">
              <div className="border rounded p-3 h-100">
                <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3">
                  Remetente
                </h6>
                <div className="mb-2">
                  <span className="text-muted small d-block">Razão Social / Nome</span>
                  <strong>{cte.remetente?.nome || "-"}</strong>
                </div>
                {cte.remetente?.nomeFantasia && (
                  <div className="mb-2">
                    <span className="text-muted small d-block">Nome Fantasia</span>
                    <span>{cte.remetente.nomeFantasia}</span>
                  </div>
                )}
                <Row className="mb-2">
                  <Col xs={6}>
                    <span className="text-muted small d-block">CNPJ / CPF</span>
                    <span>{code.formatarDocumento(cte.remetente?.documento)}</span>
                  </Col>
                  <Col xs={6}>
                    <span className="text-muted small d-block">Inscrição Estadual</span>
                    <span>{cte.remetente?.inscricaoEstadual || "ISENTO"}</span>
                  </Col>
                </Row>
                <div className="mb-2">
                  <span className="text-muted small d-block">Telefone</span>
                  <span>
                    {cte.remetente?.fone
                      ? code.FormatarTelefone(cte.remetente.fone)
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-muted small d-block">Endereço</span>
                  <span>
                    {cte.remetente
                      ? `${cte.remetente.endereco || ""}, ${
                          cte.remetente.enderecoNumero || "S/N"
                        } - ${cte.remetente.bairro || ""}, ${
                          cte.remetente.nomeMunicipio || ""
                        } - ${cte.remetente.uf || ""} - CEP: ${
                          cte.remetente.cep ? code.FormatarCEP(cte.remetente.cep) : "-"
                        }`
                      : "-"}
                  </span>
                </div>
              </div>
            </Col>

            {/* Destinatário */}
            <Col xs={12} lg={6} className="mb-3">
              <div className="border rounded p-3 h-100">
                <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3">
                  Destinatário
                </h6>
                <div className="mb-2">
                  <span className="text-muted small d-block">Razão Social / Nome</span>
                  <strong>{cte.destinatario?.nome || "-"}</strong>
                </div>
                {cte.destinatario?.nomeFantasia && (
                  <div className="mb-2">
                    <span className="text-muted small d-block">Nome Fantasia</span>
                    <span>{cte.destinatario.nomeFantasia}</span>
                  </div>
                )}
                <Row className="mb-2">
                  <Col xs={6}>
                    <span className="text-muted small d-block">CNPJ / CPF</span>
                    <span>{code.formatarDocumento(cte.destinatario?.documento)}</span>
                  </Col>
                  <Col xs={6}>
                    <span className="text-muted small d-block">Inscrição Estadual</span>
                    <span>{cte.destinatario?.inscricaoEstadual || "ISENTO"}</span>
                  </Col>
                </Row>
                <div className="mb-2">
                  <span className="text-muted small d-block">Telefone</span>
                  <span>
                    {cte.destinatario?.fone
                      ? code.FormatarTelefone(cte.destinatario.fone)
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-muted small d-block">Endereço</span>
                  <span>
                    {cte.destinatario
                      ? `${cte.destinatario.endereco || ""}, ${
                          cte.destinatario.enderecoNumero || "S/N"
                        } - ${cte.destinatario.bairro || ""}, ${
                          cte.destinatario.nomeMunicipio || ""
                        } - ${cte.destinatario.uf || ""} - CEP: ${
                          cte.destinatario.cep
                            ? code.FormatarCEP(cte.destinatario.cep)
                            : "-"
                        }`
                      : "-"}
                  </span>
                </div>
              </div>
            </Col>

            {/* Tomador (se informado como entidade separada) */}
            {cte.tomador && (
              <Col xs={12} className="mt-2">
                <div className="border rounded p-3 bg-light">
                  <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3">
                    Tomador do Serviço
                  </h6>
                  <Row>
                    <Col xs={12} md={6} className="mb-2">
                      <span className="text-muted small d-block">Razão Social / Nome</span>
                      <strong>{cte.tomador.nome}</strong>
                    </Col>
                    <Col xs={12} md={6} className="mb-2">
                      <span className="text-muted small d-block">CNPJ / CPF</span>
                      <span>{code.formatarDocumento(cte.tomador.documento)}</span>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Informações da Carga */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faBoxes} className="me-2 text-primary" />
            Informações da Carga
          </h5>
          <Row className="mb-3">
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Valor Total da Carga</span>
              <strong className="fs-5 text-success">
                {code.formatarMoeda(cte.carga?.valorCarga)}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Produto Predominante</span>
              <strong>{cte.carga?.produtoPredominante || "-"}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-muted small d-block">Outras Características</span>
              <span>{cte.carga?.outraCaracteristica || "-"}</span>
            </Col>
          </Row>

          <h6 className="fw-bold text-secondary mt-3 mb-2">Quantidades e Medidas</h6>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Tipo de Medida</th>
                  <th>Unidade</th>
                  <th className="text-end">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {cte.carga?.quantidades && cte.carga.quantidades.length > 0 ? (
                  cte.carga.quantidades.map((qtd, index) => (
                    <tr key={index}>
                      <td>{qtd.tipoMedida}</td>
                      <td>{code.getUnidadeMedidaLabel(qtd.codigoUnidadeMedida)}</td>
                      <td className="text-end fw-bold">
                        {Number(qtd.quantidadeCarga).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-2">
                      Nenhuma quantidade informada.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Documentos Originários */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2 text-primary" />
            Documentos Originários
          </h5>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "20%" }}>Tipo Documento</th>
                  <th>Chave de Acesso / Identificador</th>
                  <th style={{ width: "10%" }} className="text-center">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {cte.documentos && cte.documentos.length > 0 ? (
                  cte.documentos.map((doc, index) => (
                    <tr key={index}>
                      <td>{code.getTipoDocumentoLabel(doc.tipoDocumento)}</td>
                      <td className="font-monospace text-break">
                        {doc.chaveDocumento}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Copiar Chave"
                          onClick={() =>
                            code.copiarTexto(doc.chaveDocumento, "Chave")
                          }
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-2">
                      Nenhum documento originário vinculado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Valores da Prestação e Componentes */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <h5 className="text-primary mb-3 d-flex align-items-center">
            <FontAwesomeIcon icon={faDollarSign} className="me-2 text-primary" />
            Valores da Prestação do Serviço
          </h5>

          {/* Cards de Totais */}
          <Row className="mb-4">
            <Col xs={12} md={4} className="mb-2">
              <div className="p-3 border rounded text-center bg-light">
                <span className="text-muted small d-block">
                  Valor da Prestação do Serviço
                </span>
                <span className="fs-4 fw-bold text-dark">
                  {code.formatarMoeda(
                    cte.valoresPrestacao?.valorPrestacaoServico
                  )}
                </span>
              </div>
            </Col>
            <Col xs={12} md={4} className="mb-2">
              <div className="p-3 border rounded text-center bg-light">
                <span className="text-muted small d-block">
                  Valor Total dos Tributos
                </span>
                <span className="fs-4 fw-bold text-secondary">
                  {code.formatarMoeda(cte.valoresPrestacao?.valorTotalTributos)}
                </span>
              </div>
            </Col>
            <Col xs={12} md={4} className="mb-2">
              <div className="p-3 border rounded text-center bg-success-subtle border-success">
                <span className="text-success small fw-bold d-block">
                  VALOR TOTAL A RECEBER
                </span>
                <span className="fs-3 fw-bold text-success">
                  {code.formatarMoeda(cte.valoresPrestacao?.valorReceber)}
                </span>
              </div>
            </Col>
          </Row>

          <h6 className="fw-bold text-secondary mb-2">
            Componentes da Prestação do Serviço
          </h6>
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Nome do Componente</th>
                  <th className="text-end" style={{ width: "25%" }}>
                    Valor (R$)
                  </th>
                </tr>
              </thead>
              <tbody>
                {cte.valoresPrestacao?.componentes &&
                cte.valoresPrestacao.componentes.length > 0 ? (
                  cte.valoresPrestacao.componentes.map((comp, index) => (
                    <tr key={index}>
                      <td>{comp.nome}</td>
                      <td className="text-end fw-bold">
                        {code.formatarMoeda(comp.valor)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center text-muted py-2">
                      Nenhum componente cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Informações Complementares / Observações */}
      {cte.complemento?.observacao && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <h5 className="text-primary mb-3 d-flex align-items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
              Observações / Informações Complementares
            </h5>
            <div className="p-3 bg-light rounded border font-monospace text-break">
              {cte.complemento.observacao}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default CTeVisualizaView;
